// Single source of truth for the API-config → CSS-variable token mapping.
//
// No DOM and no `@superbright/indexeddb-orm` imports, so it is safe to use both
// during SSR (Node, via pages/_serverTheme.ts) and in the browser (via
// src/theme/themeConfig.ts). Each caller resolves its font stacks its own way
// and then calls computeThemeTokens — so the token list lives in ONE place and
// the SSR/client outputs can no longer drift apart.

export interface ThemeTokenConfig {
  heading_size?: number;
  subheading_size?: number;
  body_size?: number;
  small_body_size?: number;
  color_bg?: string;
  color_banner_bg?: string;
  color_text?: string;
  color_text_muted?: string;
  color_primary_btn_bg?: string;
  color_primary_btn_text?: string;
  color_secondary_border?: string;
  color_link?: string;
  color_card_text?: string;
}

/**
 * Build the flat CSS-variable map from a resolved theme config plus the
 * already-computed heading/body font-family stacks. Callers guarantee the
 * required colour/size fields are present (the SSR path validates them; the
 * client path receives a fully-defaulted ThemeState).
 */
export function computeThemeTokens(
  cfg: ThemeTokenConfig,
  headingFontStack: string,
  bodyFontStack: string
): Record<string, string> {
  return {
    "--font-heading": headingFontStack,
    "--font-body": bodyFontStack,
    "--font-size-h1": `${cfg.heading_size}px`,
    "--font-size-h2": `${cfg.subheading_size}px`,
    "--font-size-body": `${cfg.body_size}px`,
    "--font-size-small": `${cfg.small_body_size}px`,
    "--color-bg": cfg.color_bg!,
    // Falls back to the page background when the API omits a banner colour.
    "--color-banner-bg": cfg.color_banner_bg ?? cfg.color_bg!,
    "--color-text": cfg.color_text!,
    "--color-text-muted": cfg.color_text_muted!,
    "--color-primary-btn-bg": cfg.color_primary_btn_bg!,
    "--color-primary-btn-text": cfg.color_primary_btn_text!,
    "--color-secondary-border": cfg.color_secondary_border!,
    "--color-link": cfg.color_link!,
    "--color-card-text": cfg.color_card_text!,
  };
}
